import { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { Button, Table } from "react-bootstrap";
import { Images } from "../../../../config/images";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import SuccessModal from "../../../common/SuccessModal";
import "./Teacher.css";
import "../../../../common/styles/table.css";
import { useGetTeacherDetail } from "../../../../hooks/school/teacher/useGetTeacherDetail";
import { useParams, useNavigate } from "react-router-dom";
import { useUpdateTeacher } from "../../../../hooks/school/teacher/useUpdateTeacher";
import { useUpdateTeacherStudents } from "../../../../hooks/school/teacher/useUpdateTeacherStudents";
import { Link } from "react-router-dom";
import { teacherValidationSchema } from "./CreateTeacher";
import BackButton from "../../../common/BackButton";
import Loader from "../../../common/Loader";
import TableError from "../../common/TableError";
import { useDeleteTeacher } from "../../../../hooks/school/teacher/useDeleteTeacher";
import DeleteModal from "../../../common/DeleteModal";
import { useToggleTeacherStatus } from "../../../../hooks/school/teacher/useToggleTeacherStatus";

import TablePagination from "../../../common/pagination/TablePagination";

const EditTeacher = () => {
  const navigate = useNavigate();
  const { teacherId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Student unassign modal state
  const [showStudentDeleteModal, setShowStudentDeleteModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [unassignSuccess, setUnassignSuccess] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handleShow = () => {
    resetDeleteState(); // Reset delete state when opening modal
    setShowModal(true);
  };
  const handleClose = () => {
    resetDeleteState(); // Reset delete state when closing modal
    setShowModal(false);
  };
  const {
    teacherDetail,
    assignedStudents,
    meta,
    loading,
    error,
    getTeacherDetail,
  } = useGetTeacherDetail();
  const {
    updateTeacher,
    loading: updating,
    error: updateError,
  } = useUpdateTeacher();
  const {
    deleteTeacher,
    loading: deleteLoader,
    error: deleteError,
    success: deleteSuccess,
    resetState: resetDeleteState,
  } = useDeleteTeacher();
  const {
    updateTeacherStudents,
    loading: unassignLoading,
    error: unassignError,
  } = useUpdateTeacherStudents();

  // Toggle active / inactive status
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleSuccess, setToggleSuccess] = useState(false);
  const [teacherStatus, setTeacherStatus] = useState(null);

  const {
    toggleStatus,
    loading: toggleLoading,
    error: toggleError,
  } = useToggleTeacherStatus();

  // Seed local status from API data on first load
  useEffect(() => {
    if (teacherDetail?.status != null) {
      setTeacherStatus(teacherDetail.status);
    }
  }, [teacherDetail?.status]);

  const isActive = teacherStatus === "1";

  const handleToggleStatus = async () => {
    await toggleStatus(teacherId, (responseData) => {
      setToggleSuccess(true);
      // Update only the status from the API response — no re-fetch needed
      if (responseData?.data?.status != null) {
        setTeacherStatus(responseData.data.status);
      }
    });
  };

  const handleToggleModalClose = () => {
    setShowToggleModal(false);
    setToggleSuccess(false);
  };

  const fullName = `${teacherDetail?.firstName} ${teacherDetail?.lastName}`;

  // Fetch teacher details when ID or page changes
  useEffect(() => {
    getTeacherDetail({ teacherId, page: currentPage, per_page: perPage });
  }, [teacherId, currentPage, perPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const initialValues = teacherDetail
    ? {
      firstName: teacherDetail.firstName || "",
      lastName: teacherDetail.lastName || "",
      email: teacherDetail.email || "",
    }
    : { firstName: "", lastName: "", email: "" };

  const handleSubmit = async (values) => {
    const updatedData = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
    };

    await updateTeacher({
      teacherId,
      updatedData,
      onSuccess: () => {
        setShowSuccessModal(true);
      },
    });
  };

  const handleGoToTeacherList = () => {
    setShowSuccessModal(false);
    navigate("/school-admin/teachers");
  };

  const handleContinueEditing = () => {
    setShowSuccessModal(false);
  };

  const handleViewStudent = (student) => {
    console.log("View student:", student);
  };

  const handleDeleteStudent = (student) => {
    setSelectedStudent(student);
    setUnassignSuccess(false);
    setShowStudentDeleteModal(true);
  };

  const handleUnassignStudent = async () => {
    await updateTeacherStudents(
      {
        action: "unassign",
        teacher_id: Number(teacherId),
        student_ids: [selectedStudent.id],
      },
      () => {
        setUnassignSuccess(true);
      },
    );
  };

  const handleStudentDeleteModalClose = () => {
    setShowStudentDeleteModal(false);
    setSelectedStudent(null);
    setUnassignSuccess(false);
  };

  const handleUnassignSuccessContinue = () => {
    handleStudentDeleteModalClose();
    getTeacherDetail({ teacherId, page: currentPage, per_page: perPage });
  };

  const handleDeleteTeacher = async () => {
    await deleteTeacher(teacherId);
  };
  if (loading) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="EDIT TEACHER" />
        <Loader message="Loading Teacher Details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="EDIT TEACHER" />

        <TableError
          error={error}
          handleRefresh={() => getTeacherDetail({ teacherId })}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="pb-2">
        <BackButton />
      </div>
      <TopImage imageSrc={Images.TopImage} text="EDIT TEACHER" />

      <div className="d-flex justify-content-end align-items-center gap-3 pt-3 pb-3">
        {/* Active / Inactive toggle */}
        {teacherDetail && (
          <button
            type="button"
            className={`btn btn-sm ${
              isActive ? "btn-outline-danger" : "btn-outline-success"
            }`}
            onClick={() => {
              setToggleSuccess(false);
              setShowToggleModal(true);
            }}
          >
            <i
              className={`bi ${
                isActive ? "bi-person-slash" : "bi-person-check"
              } me-1`}
            ></i>
            {isActive ? "Deactivate Teacher" : "Activate Teacher"}
          </button>
        )}

        <div
          className="text-decoration-underline"
          style={{ cursor: "pointer", color: "var(--text-color)" }}
        >
          <Link onClick={() => handleShow()}>Delete Teacher</Link>

          <DeleteModal
            showModal={showModal}
            handleClose={handleClose}
            message="Are you sure you want to delete this Teacher? This action cannot be undone."
            loading={deleteLoader}
            handleDelete={handleDeleteTeacher}
            error={deleteError}
            success={deleteSuccess}
            successMessage="Teacher has been deleted successfully."
            onSuccessContinue={() => navigate("/school-admin/teachers")}
          />
        </div>
      </div>

      <div className="custom-card">
        {error && <div className="alert alert-danger text-center">{error}</div>}

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={teacherValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <div className="row">
                <div className="col-md-6">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
              </div>

              <CustomInput
                label="Email Address"
                name="email"
                type="email"
                placeholder="Enter email address"
                className="custom-primary-input"
                layout="stacked"
              />

              {/* Current Status Display */}
              {teacherDetail && (
                <div>
                  <label
                    className="form-label"
                    style={{ color: "var(--text-color)" }}
                  >
                    Status
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className={`badge ${
                        isActive ? "bg-success" : "bg-danger"
                      }`}
                      style={{ fontSize: "13px", padding: "6px 12px" }}
                    >
                      <i
                        className={`bi ${
                          isActive
                            ? "bi-check-circle-fill"
                            : "bi-x-circle-fill"
                        } me-1`}
                      ></i>
                      {isActive ? "Active" : "Inactive"}
                    </span>
                    <span
                      className="text-muted"
                      style={{ fontSize: "12px" }}
                    >
                      {isActive
                        ? "This teacher can log in and access the platform."
                        : "This teacher cannot log in or access the platform."}
                    </span>
                  </div>
                </div>
              )}

              {/* Assigned Students Count */}
              <div>
                <label
                  className="form-label"
                  style={{ color: "var(--text-color)" }}
                >
                  Assigned Students
                </label>
                <input
                  type="text"
                  className="custom-primary-input form-control"
                  value={assignedStudents.length}
                  readOnly
                  disabled
                />
              </div>

              {/* Assigned Students Table */}
              <div>
                <label
                  className="form-label"
                  style={{ color: "var(--text-color)" }}
                >
                  Assigned Students List
                </label>
                <div className="custom-table-container">
                  <div
                    className="custom-table-wrapper"
                    style={{ maxHeight: "400px" }}
                  >
                    <Table hover className="custom-table">
                      <thead className="custom-table-head">
                        <tr>
                          <th>Student ID</th>
                          <th>Student Name</th>
                          <th>Email</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="4" className="text-center py-3">
                              Loading...
                            </td>
                          </tr>
                        ) : assignedStudents.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-3">
                              No students assigned yet
                            </td>
                          </tr>
                        ) : (
                          assignedStudents.map((student) => (
                            <tr key={student.id}>
                              <td className="custom-id">{student?.username}</td>
                              <td>{student?.name}</td>
                              <td>{student?.email}</td>
                              <td>
                                <div className="d-flex gap-3 align-items-center">
                                  <button
                                    type="button"
                                    className="btn btn-dark btn-sm"
                                    onClick={() => handleDeleteStudent(student)}
                                  >
                                    Unassign
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
                {meta && (
                  <TablePagination
                    currentPage={meta.current_page}
                    totalPage={meta.last_page}
                    onPageChange={handlePageChange}
                    disabled={loading}
                  />
                )}

                {/* Student Unassign Modal */}
                <DeleteModal
                  showModal={showStudentDeleteModal}
                  handleClose={handleStudentDeleteModalClose}
                  message={`Are you sure you want to unassign ${selectedStudent?.name} from this teacher?`}
                  loading={unassignLoading}
                  handleDelete={handleUnassignStudent}
                  error={unassignError}
                  success={unassignSuccess}
                  successMessage="Student has been unassigned successfully."
                  onSuccessContinue={handleUnassignSuccessContinue}
                  customSuccessTitle="Unassigned Successfully"
                  customErrorTitle="Unassign Failed"
                  customConfirmTitle="Confirm Unassign"
                  customActionButtonText="Unassign"
                  customLoadingButtonText="Unassigning..."
                />
              </div>

              <div>
                {updateError && (
                  <p className="custom-error pb-2">{updateError}</p>
                )}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || updating}
                >
                  {isSubmitting || updating ? "Saving..." : "Save"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Teacher Updated Successfully"
        message="The teacher details have been updated successfully."
        primaryButtonText="Continue Editing"
        secondaryButtonText="Teacher List"
        onPrimaryClick={handleContinueEditing}
        onSecondaryClick={handleGoToTeacherList}
      />

      {/* Toggle Status Modal */}
      <DeleteModal
        showModal={showToggleModal}
        handleClose={handleToggleModalClose}
        message={`Are you sure you want to ${
          isActive ? "deactivate" : "activate"
        } this teacher? ${
          isActive
            ? "They will no longer be able to log in or access the platform."
            : "They will regain access to log in and use the platform."
        }`}
        loading={toggleLoading}
        handleDelete={handleToggleStatus}
        error={toggleError}
        success={toggleSuccess}
        successMessage={`Teacher has been ${
          isActive ? "deactivated" : "activated"
        } successfully.`}
        onSuccessContinue={handleToggleModalClose}
        customSuccessTitle={isActive ? "Deactivated" : "Activated"}
        customErrorTitle={isActive ? "Deactivation Failed" : "Activation Failed"}
        customConfirmTitle={isActive ? "Deactivate Teacher" : "Activate Teacher"}
        customActionButtonText={isActive ? "Deactivate" : "Activate"}
        customLoadingButtonText={isActive ? "Deactivating..." : "Activating..."}
        isToggle={true}
        isActive={isActive}
      />
    </div>
  );
};

export default EditTeacher;
